import { useEffect, useMemo, useState, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { EmptyState } from "@/components/common/EmptyState";
import { PageHeader } from "@/components/layout/PageHeader";
import { OPS } from "@/constants/testIds";
import { fmtInt, fmtNumber, localeDate } from "@/lib/format";
import {
    ClipboardList,
    ArrowUpRight,
    ArrowDownRight,
    Store,
    CalendarRange,
    ChevronRight,
    Smartphone,
} from "lucide-react";

// Column defs — order & meta shared between header, body, and totals row.
// unit "currency" -> 2 dp, "number" -> 0 dp.
const COLS = [
    { key: "total", labelKey: "dsr_col_total", unit: "currency" },
    { key: "walk_in", labelKey: "dsr_col_walk_in", unit: "currency", channel: true },
    { key: "cc", labelKey: "dsr_col_cc", unit: "currency", channel: true },
    { key: "apps", labelKey: "dsr_col_apps", unit: "currency", channel: true },
    { key: "total_tc", labelKey: "dsr_col_total_tc", unit: "number" },
    { key: "cc_tc", labelKey: "dsr_col_cc_tc", unit: "number" },
    { key: "apps_tc", labelKey: "dsr_col_apps_tc", unit: "number" },
    { key: "avg_ticket", labelKey: "dsr_col_avg_ticket", unit: "currency" },
];

// The "months this branch has data for" list is built from a
// coverage_to/coverage_from meta pull, mirroring how PeriodPicker works. To
// avoid another endpoint we generate the last 12 months of the network
// coverage window.
function buildMonthList(coverage_from, coverage_to) {
    if (!coverage_from || !coverage_to) return [];
    const [fy, fm] = coverage_from.slice(0, 7).split("-").map(Number);
    const [ty, tm] = coverage_to.slice(0, 7).split("-").map(Number);
    const out = [];
    let y = fy;
    let m = fm;
    while (y < ty || (y === ty && m <= tm)) {
        out.push(`${y}-${String(m).padStart(2, "0")}`);
        m += 1;
        if (m > 12) {
            m = 1;
            y += 1;
        }
    }
    return out.reverse(); // most-recent first
}

// bilingual month label
function monthLabel(ym, lang) {
    if (!ym) return "";
    const [yy, mm] = ym.split("-").map(Number);
    return new Date(Date.UTC(yy, mm - 1, 1)).toLocaleDateString(
        lang === "ar" ? "ar-SA" : "en-US",
        { month: "long", year: "numeric" }
    );
}

// ---------------------------------------------------------------- helpers
function DashOrValue({ value, unit, lang }) {
    if (value === null || value === undefined) {
        return <span className="text-[color:var(--ink-muted)]">{"\u2014"}</span>;
    }
    if (unit === "number") return <>{fmtInt(value, lang)}</>;
    return <>{fmtNumber(value, lang, 2)}</>;
}

// Channel amount + its share (%) of that period's total sales. Shown for
// walk-in / call-center / delivery-apps on every daily row AND on the
// cumulative month totals row. Percentage is null-safe (hidden when the
// amount or the total is missing/zero).
function ChannelValue({ value, total, unit, lang }) {
    const pct = (value !== null && value !== undefined && total)
        ? (value / total) * 100
        : null;
    return (
        <div className="flex flex-col items-end leading-tight">
            <span><DashOrValue value={value} unit={unit} lang={lang} /></span>
            {pct !== null ? (
                <span className="text-[10px] font-medium text-[color:var(--brand-orange)] tnum" data-testid="dsr-channel-pct">
                    <bdi dir="ltr">{fmtNumber(pct, lang, 1)}%</bdi>
                </span>
            ) : null}
        </div>
    );
}

function DeltaCell({ pct, lang }) {
    if (pct === null || pct === undefined) {
        return <span className="text-[color:var(--ink-muted)]">{"\u2014"}</span>;
    }
    const up = pct >= 0;
    const Icon = up ? ArrowUpRight : ArrowDownRight;
    return (
        <span
            className={`inline-flex items-center justify-end gap-0.5 text-xs tnum font-semibold ${
                up ? "text-[color:var(--success)]" : "text-[color:var(--danger)]"
            }`}
        >
            <Icon className="h-3.5 w-3.5 shrink-0" />
            <bdi dir="ltr">{fmtNumber(Math.abs(pct), lang, 1)}%</bdi>
        </span>
    );
}

// ---------------------------------------------------------------- DSR table
function DsrTable({ data, lang, t }) {
    const days = data?.days || [];
    const totals = data?.totals || {};
    const prev = data?.prev || {};
    const prevTotals = prev.totals || {};
    const deltas = prev.deltas || {};
    const weekdayNames = t("dsr_wk");

    // rows expanded to show their per-app breakdown (Set of iso dates)
    const [expanded, setExpanded] = useState(new Set());
    const toggleRow = (iso) =>
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(iso)) next.delete(iso);
            else next.add(iso);
            return next;
        });

    return (
        <div className="overflow-x-auto rounded-lg border border-[color:var(--border-soft)] max-h-[600px] overflow-y-auto scrollbar-thin">
            <Table data-testid={OPS.dsr.table}>
                <TableHeader className="sticky top-0 z-10">
                    <TableRow className="bg-[color:rgba(224,222,217,0.92)] hover:bg-[color:rgba(224,222,217,0.92)]">
                        <TableHead className="text-start whitespace-nowrap">{t("dsr_col_date")}</TableHead>
                        {COLS.map((c) => (
                            <TableHead key={c.key} className="text-end whitespace-nowrap">
                                {t(c.labelKey)}
                            </TableHead>
                        ))}
                    </TableRow>
                </TableHeader>

                <TableBody>
                    {days.map((d) => {
                        const isOpen = expanded.has(d.date);
                        const canOpen = !!d.has_apps_breakdown;
                        return (
                            <Fragment key={d.date}>
                                <TableRow
                                    data-testid={OPS.dsr.row(d.date)}
                                    onClick={canOpen ? () => toggleRow(d.date) : undefined}
                                    aria-expanded={canOpen ? isOpen : undefined}
                                    className={`hover:bg-[color:rgba(186,217,235,0.18)] ${
                                        canOpen ? "cursor-pointer" : ""
                                    }`}
                                >
                                    <TableCell className="font-medium text-[color:var(--ink)] whitespace-nowrap">
                                        <div className="flex items-center gap-1.5">
                                            {canOpen ? (
                                                <ChevronRight
                                                    data-testid={OPS.dsr.expandBtn(d.date)}
                                                    className={`h-3.5 w-3.5 shrink-0 text-[color:var(--ink-muted)] transition-transform duration-150 ${
                                                        // isOpen -> point down (rotate-90 in LTR, rotate-90 also
                                                        // works for RTL because Chevron>). Closed -> right in LTR,
                                                        // mirrored for RTL so it points at the row's start edge.
                                                        isOpen ? "rotate-90" : lang === "ar" ? "rotate-180" : ""
                                                    }`}
                                                />
                                            ) : (
                                                <span className="w-3.5" />
                                            )}
                                            <div className="flex flex-col leading-tight">
                                                <bdi dir="ltr" className="text-xs">{localeDate(d.date, lang)}</bdi>
                                                <span className="text-[10px] text-[color:var(--ink-muted)]">
                                                    {weekdayNames[d.weekday] ?? ""}
                                                </span>
                                            </div>
                                        </div>
                                    </TableCell>
                                    {COLS.map((c) => (
                                        <TableCell
                                            key={c.key}
                                            className="text-end tnum text-[color:var(--ink)] whitespace-nowrap"
                                            title={
                                                c.key === "apps_tc" && d.apps_tc === null
                                                    ? t("dsr_apps_tc_note")
                                                    : undefined
                                            }
                                        >
                                            {c.channel ? (
                                                <ChannelValue value={d[c.key]} total={d.total} unit={c.unit} lang={lang} />
                                            ) : (
                                                <DashOrValue value={d[c.key]} unit={c.unit} lang={lang} />
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>

                                {isOpen && canOpen ? (
                                    <TableRow
                                        data-testid={OPS.dsr.appsRow(d.date)}
                                        className="bg-[color:rgba(186,217,235,0.10)]"
                                    >
                                        <TableCell
                                            colSpan={COLS.length + 1}
                                            className="py-2 px-3"
                                        >
                                            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-[color:var(--ink-muted)] mb-2">
                                                <Smartphone className="h-3.5 w-3.5" />
                                                {t("dsr_apps_breakdown")}
                                            </div>
                                            <div className="overflow-x-auto">
                                                <table className="w-full text-xs">
                                                    <thead>
                                                        <tr className="text-[color:var(--ink-muted)]">
                                                            <th className="text-start font-medium px-2 py-1">
                                                                {t("dsr_apps_col_name")}
                                                            </th>
                                                            <th className="text-end font-medium px-2 py-1">
                                                                {t("dsr_apps_col_net")}
                                                            </th>
                                                            <th className="text-end font-medium px-2 py-1">
                                                                {t("dsr_apps_col_orders")}
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {d.apps_list.map((a) => (
                                                            <tr
                                                                key={a.app_name}
                                                                data-testid={OPS.dsr.appsRowApp(d.date, a.app_name)}
                                                                className="border-t border-[color:var(--border-soft)]/60"
                                                            >
                                                                <td className="px-2 py-1 text-[color:var(--ink)]">
                                                                    <bdi>{a.app_name}</bdi>
                                                                </td>
                                                                <td className="px-2 py-1 text-end tnum text-[color:var(--ink)]">
                                                                    {fmtNumber(a.net, lang, 2)}
                                                                </td>
                                                                <td className="px-2 py-1 text-end tnum text-[color:var(--ink)]">
                                                                    {fmtInt(a.orders, lang)}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ) : null}
                            </Fragment>
                        );
                    })}
                </TableBody>

                <TableFooter>
                    <TableRow
                        data-testid={OPS.dsr.totalsRow}
                        className="bg-[color:rgba(224,222,217,0.5)] hover:bg-[color:rgba(224,222,217,0.5)] font-bold"
                    >
                        <TableCell className="text-[color:var(--ink)] whitespace-nowrap">
                            {t("dsr_row_totals")}
                        </TableCell>
                        {COLS.map((c) => (
                            <TableCell key={c.key} className="text-end tnum text-[color:var(--ink)] whitespace-nowrap">
                                {c.channel ? (
                                    <ChannelValue value={totals[c.key]} total={totals.total} unit={c.unit} lang={lang} />
                                ) : (
                                    <DashOrValue value={totals[c.key]} unit={c.unit} lang={lang} />
                                )}
                            </TableCell>
                        ))}
                    </TableRow>
                    <TableRow
                        data-testid={OPS.dsr.prevRow}
                        className="bg-[color:rgba(224,222,217,0.28)] hover:bg-[color:rgba(224,222,217,0.28)]"
                    >
                        <TableCell className="text-[color:var(--ink-muted)] whitespace-nowrap">
                            {t("dsr_row_prev")}
                            <span className="ms-1 text-[11px]">
                                <bdi dir="ltr">{lang === "ar" ? prev.month_label_ar : prev.month_label_en}</bdi>
                            </span>
                        </TableCell>
                        {COLS.map((c) => (
                            <TableCell key={c.key} className="text-end tnum text-[color:var(--ink-muted)] whitespace-nowrap">
                                {c.channel ? (
                                    <ChannelValue value={prevTotals[c.key]} total={prevTotals.total} unit={c.unit} lang={lang} />
                                ) : (
                                    <DashOrValue value={prevTotals[c.key]} unit={c.unit} lang={lang} />
                                )}
                            </TableCell>
                        ))}
                    </TableRow>
                    <TableRow
                        data-testid={OPS.dsr.deltaRow}
                        className="bg-[color:rgba(186,217,235,0.14)] hover:bg-[color:rgba(186,217,235,0.14)]"
                    >
                        <TableCell className="text-[color:var(--ink)] whitespace-nowrap font-semibold">
                            {t("dsr_row_delta")}
                        </TableCell>
                        {COLS.map((c) => (
                            <TableCell key={c.key} className="text-end whitespace-nowrap">
                                <DeltaCell pct={deltas[c.key]} lang={lang} />
                            </TableCell>
                        ))}
                    </TableRow>
                </TableFooter>
            </Table>
        </div>
    );
}

const ALL_BRANCHES = "__all__";

// -------------------------------------------------- all-branches comparison
// One row PER branch (its monthly totals + channel split) plus a network-total
// footer, so the whole network's sales performance is comparable in one table.
function AllBranchesTable({ data, lang, t }) {
    const rows = data?.rows || [];
    const net = data?.network || {};
    const bname = (b) => (lang === "ar" ? (b?.name_ar || b?.name_en) : (b?.name_en || b?.name_ar)) || "—";
    return (
        <div className="overflow-x-auto rounded-lg border border-[color:var(--border-soft)] max-h-[600px] overflow-y-auto scrollbar-thin">
            <Table data-testid={OPS.dsr.allTable}>
                <TableHeader className="sticky top-0 z-10">
                    <TableRow className="bg-[color:rgba(224,222,217,0.92)] hover:bg-[color:rgba(224,222,217,0.92)]">
                        <TableHead className="text-start whitespace-nowrap">{t("dsr_col_branch")}</TableHead>
                        {COLS.map((c) => (
                            <TableHead key={c.key} className="text-end whitespace-nowrap">{t(c.labelKey)}</TableHead>
                        ))}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((r) => (
                        <TableRow
                            key={r.branch.id}
                            data-testid={OPS.dsr.allRow(r.branch.id)}
                            className={`hover:bg-[color:rgba(186,217,235,0.18)] ${r.has_data ? "" : "opacity-60"}`}
                        >
                            <TableCell className="text-start font-semibold text-[color:var(--ink)] whitespace-nowrap">
                                <bdi>{bname(r.branch)}</bdi>
                            </TableCell>
                            {COLS.map((c) => (
                                <TableCell key={c.key} className="text-end tnum text-[color:var(--ink)] whitespace-nowrap">
                                    {c.channel ? (
                                        <ChannelValue value={r[c.key]} total={r.total} unit={c.unit} lang={lang} />
                                    ) : (
                                        <DashOrValue value={r[c.key]} unit={c.unit} lang={lang} />
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
                <TableFooter>
                    <TableRow
                        data-testid={OPS.dsr.networkRow}
                        className="bg-[color:rgba(232,168,80,0.16)] hover:bg-[color:rgba(232,168,80,0.16)] font-bold"
                    >
                        <TableCell className="text-start font-bold text-[color:var(--ink)] whitespace-nowrap">
                            {t("dsr_network_total")}
                        </TableCell>
                        {COLS.map((c) => (
                            <TableCell key={c.key} className="text-end tnum font-bold text-[color:var(--ink)] whitespace-nowrap">
                                {c.channel ? (
                                    <ChannelValue value={net[c.key]} total={net.total} unit={c.unit} lang={lang} />
                                ) : (
                                    <DashOrValue value={net[c.key]} unit={c.unit} lang={lang} />
                                )}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableFooter>
            </Table>
        </div>
    );
}


// ---------------------------------------------------------------- page
export default function BranchDsrPage() {
    const { me } = useAuth();
    const { t, tn, lang } = useI18n();

    const branches = useMemo(() => {
        const list = me?.sales?.branches || [];
        // DSR is per-branch: NO "all" option (data is only meaningful per branch)
        return list;
    }, [me]);

    // ?branch=<id> (e.g. a figure linked from «التحليل الذكي») picks that branch when the user may see it
    const [searchParams] = useSearchParams();
    const wanted = searchParams.get("branch");
    const pick = (list) => (list.some((b) => b.id === wanted) ? wanted : list[0]?.id || "");
    const [branchId, setBranchId] = useState(() => pick(branches));
    const [meta, setMeta] = useState(null);
    const [months, setMonths] = useState([]);
    const [month, setMonth] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    // pick a default branch as soon as we know the user's permitted list
    useEffect(() => {
        if (!branchId && branches.length) setBranchId(pick(branches));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [branches, branchId]);

    // load meta for month list (uses the existing /sales/meta endpoint)
    useEffect(() => {
        let alive = true;
        api.get("/sales/meta")
            .then((r) => alive && setMeta(r.data))
            .catch(() => alive && setMeta(null));
        return () => {
            alive = false;
        };
    }, []);

    useEffect(() => {
        if (!meta?.coverage_from || !meta?.coverage_to) {
            setMonths([]);
            return;
        }
        const list = buildMonthList(meta.coverage_from, meta.coverage_to);
        setMonths(list);
        if (list.length && !month) setMonth(list[0]);
    }, [meta, month]);

    useEffect(() => {
        if (!branchId || !month) {
            setData(null);
            return;
        }
        let alive = true;
        setLoading(true);
        const isAll = branchId === ALL_BRANCHES;
        const req = isAll
            ? api.get("/sales/branch-dsr-all", { params: { month } })
            : api.get("/sales/branch-dsr", { params: { branch_id: branchId, month } });
        req
            .then((r) => alive && setData({ ...r.data, _all: isAll }))
            .catch(() => alive && setData(null))
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, [branchId, month]);

    const isAll = branchId === ALL_BRANCHES;

    const branchName = isAll ? t("dsr_all_branches") : tn(branches.find((b) => b.id === branchId));
    const cov = data?.cc_coverage || {};
    const covLabel = cov.from
        ? `${t("dsr_cc_window_from")} ${localeDate(cov.from, lang)} ${t("dsr_cc_window_to")} ${localeDate(cov.to, lang)}`
        : t("dsr_cc_no_coverage");

    return (
        <div data-testid={OPS.dsr.page} className="space-y-5">
            <PageHeader
                icon={ClipboardList}
                title={t("dsr_title")}
                subtitle={t("dsr_subtitle")}
                actions={
                    <>
                        <Select value={branchId} onValueChange={setBranchId} disabled={!branches.length}>
                            <SelectTrigger data-testid={OPS.dsr.branchSelect} className="lg:w-[220px]">
                                <SelectValue placeholder={t("dsr_pick_branch")} />
                            </SelectTrigger>
                            <SelectContent>
                                {branches.length > 1 ? (
                                    <SelectItem value={ALL_BRANCHES}>{t("dsr_all_branches")}</SelectItem>
                                ) : null}
                                {branches.map((b) => (
                                    <SelectItem key={b.id} value={b.id}>
                                        {tn(b)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={month} onValueChange={setMonth} disabled={!months.length}>
                            <SelectTrigger data-testid={OPS.dsr.monthSelect} className="lg:w-[200px]">
                                <SelectValue placeholder={t("dsr_month")} />
                            </SelectTrigger>
                            <SelectContent>
                                {months.map((ym) => (
                                    <SelectItem key={ym} value={ym}>
                                        {monthLabel(ym, lang)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </>
                }
            />

            <Card
                className="surface border-[color:var(--border-soft)] rounded-xl shadow-[var(--shadow-sm)]"
            >
                <CardHeader className="space-y-3 pb-4">
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                            <CardTitle className="flex items-center gap-2 text-base font-bold text-[color:var(--ink)]">
                                <ClipboardList className="h-[18px] w-[18px] text-[color:var(--brand-brown)]" />
                                {branchName || t("dsr_pick_branch")}
                                {data?.month_label_en ? (
                                    <span className="ms-2 text-xs font-medium text-[color:var(--ink-muted)]">
                                        · {lang === "ar" ? data.month_label_ar : data.month_label_en}
                                    </span>
                                ) : null}
                            </CardTitle>
                            <p className="mt-1 text-xs text-[color:var(--ink-muted)]">
                                {isAll ? t("dsr_all_subtitle") : t("dsr_subtitle")}
                            </p>
                        </div>
                        {isAll ? null : (
                        <Badge
                            data-testid={OPS.dsr.coverageBadge}
                            variant="secondary"
                            className="bg-[color:rgba(186,217,235,0.5)] text-[color:var(--brand-brown)] border-0 font-medium"
                        >
                            {covLabel}
                        </Badge>
                        )}
                    </div>
                </CardHeader>

                <CardContent>
                    {!branchId ? (
                        <EmptyState
                            icon={Store}
                            title={t("dsr_pick_branch")}
                            hint={t("dsr_subtitle")}
                            testId={OPS.dsr.empty}
                        />
                    ) : loading ? (
                        <div className="space-y-3">
                            <Skeleton className="h-8 w-full" />
                            <Skeleton className="h-64 w-full" />
                        </div>
                    ) : !data?.data_available ? (
                        <EmptyState
                            icon={ClipboardList}
                            title={t("dsr_no_data")}
                            hint={t("dsr_no_data_hint")}
                            testId={OPS.dsr.empty}
                            dataset="sales_daily_branch"
                            month={month}
                        />
                    ) : (
                        isAll ? <AllBranchesTable data={data} lang={lang} t={t} /> : <DsrTable data={data} lang={lang} t={t} />
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
